import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { SubmitMode } from "../theme/preferences";
import {
  useComposerUpload,
  type ComposerNoticeKind,
  type UseComposerUploadOptions,
} from "../composables/useComposerUpload";
import {
  useComposerDropTarget,
  type DragLike,
} from "../composables/useComposerDropTarget";
import type { AttachmentMeta } from "../assembly/input/attachments";
import { tokenize, isCommandLead } from "../assembly/input/tokenize";
import { COMMANDS, unknownCommandNotice } from "../assembly/input/commands";
import {
  MENU_ACTION, applyPick, createCommandSource, createInputMenu, createRefSource, emptyMenuState,
  type MenuItem,
} from "../assembly/input/menu";
import { modelsUsable } from "../theme/modelCatalog";
import { resolveEffectiveModel, modelGuardNote } from "../components/composer/modelFallback";
import { useComposerVoice, type UseComposerVoiceOptions, type NotifyFn, type VoiceNotice } from "../composables/useComposerVoice";
// ★FE-E3（账行 L-PF-ASR-1）：语音转写通道＝`transport/stt.ts`（唯一出网入口，逐字 `POST /api/stt`）。
//   缺省注入实装（既有两态范式：组件缺省直走 transport，注入面仅单测替身——同 `SettingsPanel`
//   的 `currentActor/logout` 缺省口径）；`voiceDeps.sttApi` 显式给值 ⇒ 替身覆盖（单测）。
import { sttApi as sttTransport } from "../transport/stt";
import {
  ComposerDropOverlay, ComposerUploadBar, type ComposerUploadBarProps,
} from "../components/composer";
import "../styles/composer.css";
import { XgIcon } from "../components/icons";
import { VoiceBar, type VoiceCaptureSource } from "../views/voiceBar";
import { SHELL_DECL } from "./shellDecl";
import type { CommandOutboxEntry } from "./useProjection";
import { resolveSubmitMode, submitButtonLabel } from "../assembly/input/submitMode";
import { useComposerQueue } from "./useComposerQueue";

/**
 * shell/Composer.tsx —— **composer 上下文堆栈 ＋ 输入区**（壳规范 §4；§1bis；§7）。
 *
 * 规范逐字：
 *  · §4 上下文堆栈：**活跃任务条 → 待办 → 接管态（审批/提问）**；输入区：编辑器·引用 chip·命令·附件·**发送⇄停止**。
 *  · §1bis：输入区**固定底部**：`padding-bottom: env(safe-area-inset-bottom)`；**`visualViewport` 适配键盘弹起**；
 *    **发送⇄停止**；附件与命令进「＋」聚合菜单。
 *  · §7：`Cmd/Ctrl+Enter` 模式取自设置面（**同一份事实**）；繁忙时投递＝**插话（steer）**（唯一真实繁忙通道）。
 * 纪律：**R3 动作面＝焦点**（审批/提问＝待办徽标入口 → **接管 composer**，**不常驻侧栏**）；
 *      字段零取数；色/间距走 token；无会话 ⇒ inert（不假装可用）。
 *
 * A2 输入区链（hermes-w；102 `Composer.vue`(792) U-1d/U-1e 段逐条译 React+TS）：
 *  · ① 上传链状态机＝`composables/useComposerUpload.ts`（submitting 守卫／>250ms 轮询等待态／
 *    B-21 三坑照抄）；**单源 inlineNotice**（`role="alert"`，唯一提示落点，禁静默）。
 *  · ② 有序草稿附件栏＝`ComposerUploadBar`（壳：可删可排序，排序＝显式 ↑↓ 动作）。
 *  · ③ 全视口拖放覆盖层＝`ComposerDropOverlay` ＋ `composables/useComposerDropTarget.ts`
 *    （window 级监听自持，dragCounter 计数防子元素穿梭闪断）。
 *  · ④ 语音位＝**接线 `views/voiceBar.tsx`**（既有 ASR 录音条；absent(declared) ⇒ 录音入口不显示）。
 */

/** 提交附件载荷（仅草稿附件栏**非空**时随第二参上抛；空附件栏 ⇒ 单参调用＝逐字节一致） */
export interface ComposerSubmitFilesPayload {
  files: readonly AttachmentMeta[];
  sessionId: string;
  /** ★20261011（大王决策「插话/排队照抄 dsh」）：投递种类（缺省 `command`；`steer`＝繁忙期插话）。
   *  宿主（`useProjection`）据此归一为线上帧 `kind`（`transport/command.ts` 同一构造面）。 */
  kind?: "command" | "steer";
  /** ★F-38（U-S5 采甲）：**决议出的有效模型**（会话级 `model` ⇒ 全局默认兜底；`modelFallback.resolveEffectiveModel`）。
   *  发送必真取到模型再上行——禁「无 model 也发空请求」（任务书禁项）；宿主可据此随帧上行。 */
  model?: string;
}

// ── ② 有序草稿附件栏 ／ ③ 全视口拖放覆盖层：F2 批落位 `components/composer/*`（102
//    `ComposerUploadBar.vue`/`ComposerDropOverlay.vue` 逐条译件在彼处；此处只做装配，
//    既有导出面（`ComposerUploadBar`/`ComposerDropOverlay`/`ComposerUploadBarProps`）保持不变）──
export { ComposerDropOverlay, ComposerUploadBar };
export type { ComposerUploadBarProps };

/**
 * ★20261011（大王口径「发送成功不用在输入框底部…打对号，收入失败更不用」）：
 *   原 `outboxState()`／`OutboxStateView`（`已受理中／已受理／已拒绝` ＋ ✓ 字形）随「上行回执行」整体退役
 *   —— 两路调研一致：dsh 与旧树**都无**「发送成功状态行/对号」（dsh 成功＝乐观提交＋流回显；旧树 `setNotice('')` 静默）。
 *   失败面不丢：`rejected`/`error` 仍走**行内 notice**（`composer-error`）＋文本放回输入框（见 `submit`／effect）。
 */

export interface ComposerProps {
  /** 无会话 ⇒ 输入区 inert（§1：常驻底部，无会话时 inert） */
  sessionId?: string;
  submitMode: SubmitMode;
  /** 待办计数（→ 接管态入口） */
  pending: number;
  /** 活跃任务条文本（缺席 ⇒ 不出条；不补造） */
  activeTask?: string;
  /** 接管态（审批/提问）呈现件：**焦点呈现**，非常驻 */
  takeover?: React.ReactNode;
  takeoverOpen: boolean;
  onOpenTakeover?: () => void;
  /** 文本提交（空附件栏 ⇒ 单参、逐字节一致）；附件回执 metas＋sessionId 走第二参。
   *  ★A-044②（20261006）：返回 `false` ＝ **发送失败**（输入保留＋显式原因）；`true`／不返回 ＝ 成功 ⇒ 输入清空。 */
  onCommand?: (text: string, payload?: ComposerSubmitFilesPayload) => boolean | void;
  /** 命令提交（102 `emit('submit', {kind:'command', …})` 同义）：frozen 语义由宿主落。
   *  ★A-044②：返回 `false` ＝ 提交失败（输入保留＋原因）；其余 ⇒ 成功清空。 */
  onCommandLine?: (payload: { name: string; argument: string; mode: "frozen" }) => boolean | void;
  /** 发送⇄停止 */
  running?: boolean;
  /** 已请求停止、终态未到（§7.4 意图时刻 ⇒ 「停止中…」＋禁用） */
  stopping?: boolean;
  onStop?: () => void;
  /** 手机档：附件/命令进「＋」聚合菜单 */
  compact?: boolean;
  /** A 方案：无会话提交附件 ⇒ 先经此建会话（未注入 ⇒ 显式 `blocked:'no-session'`） */
  ensureSession?: () => Promise<string> | string;
  /** 上传状态机依赖注入面 */
  uploadDeps?: UseComposerUploadOptions;
  /** 语音位＝views/voiceBar.tsx 接线 */
  voiceRunRef?: string;
  voiceAbsent?: boolean;
  voiceCapture?: VoiceCaptureSource;
  /** ★20261011（大王决策「语音落点参照旧树」）：语音识别完成后是否自动发送。
   *  旧树依据＝`views/ChatView.vue:461`（识别完**直接** `sendMessage`，无二次确认）。
   *  ⇒ **缺省真**（＝旧树行为）；显式传 `false` 才退化为「转写入框待确认」（保留该路径，非默认）。 */
  voiceAutoSend?: boolean;
  /** ★20261011（大王决策「插话/排队照抄 dsh」）：繁忙期 Enter/主按钮投递偏好（dsh `settings.enter`「繁忙时的发送行为」）。
   *  缺省 `queue`（＝dsh 缺省 Queue）；`steer`＝插话。加速手势（Cmd/Ctrl+Enter）恒走 steer。 */
  busyEnterMode?: "queue" | "steer";
  /** U-1e：语音依赖注入面（102 `voiceDeps` 同名 prop：单测替身 sttApi/mediaApi/recorderFactory/isSecureContext） */
  voiceDeps?: UseComposerVoiceOptions;
  /** 模型位（102 U-1a）：当前已选模型 id／目录派生结果／面板开启态／点开回调 */
  model?: string;
  modelCatalog?: { currentName?: string; groups?: unknown } | null;
  /** ★F-38（L-PF-DFT-1 采甲·U-S5）：全局默认模型（调用方从配置读取位注入：偏好/声明/模型配置，**禁硬编码模型名**） */
  fallbackModel?: string | null;
  /** ★F-38：全局默认取数失败原因（非空 ⇒ 显式拒＋带码可读错误；禁静默放行、禁编造默认） */
  fallbackReadError?: string | null;
  modelPanelOpen?: boolean;
  onOpenModel?: () => void;
  /** `@` 引用候选名字表（空表不伪装成有候选） */
  refCandidates?: readonly string[];
  /**
   * 上行命令回执行（乐观「已受理中」→ 服务端 `command_ack` 三态；缺省 ⇒ 不出条）。
   * `rejected`／`error` ⇒ 显式错误 ＋ **把该条文本放回输入框**（可重发；禁静默吞、禁编造助手内容）。
   */
  outbox?: readonly CommandOutboxEntry[];
  /** 草稿真源镜像 {content, files}（父层持有；本组件只做镜像与回流） */
  draft?: { content?: string; files?: readonly unknown[] } | null;
  onDraftChange?: (draft: { content: string; files: readonly unknown[] }) => void;
  /** e2e 锚点（宿主声明，锚点字面量不动） */
  inputTestid?: string;
  sendTestid?: string;
  stopTestid?: string;
  modelSlotTestid?: string;
}

/** 组件对外能力（102 `defineExpose` 同义） */
export interface ComposerHandle {
  submit: () => void | Promise<void>;
  cancel: () => void;
  focus: () => void;
  openFilePicker: () => void;
}

/**
 * Composer —— 输入区（**整件重抄自 102 `components/Composer.vue`(792 行)**，Vue→React+TS）。
 * 逐条承接（本件覆盖，勿再视为缺项）：① 命令词法＋命令菜单（`assembly/input/{tokenize,commands,menu}`）
 * ② 提交守卫/主按钮语义（send⇄steer⇄stop⇄stopping，§7.3(d)/§7.4）③ 未知命令**行内提示**（不静默降级）
 * ④ 模型位＋`modelsUsable` 守卫（无可用模型 ⇒ 发送禁用，停用而非隐藏）⑤ 引用 chip（`@name` → chip，可删）
 * ⑥ 接管态槽（单源 `takeover`）⑦ 上传链＋拖放层（已接线）⑧ 镜像双向同步（U-1c-FIX：对象换引用＝一次真源断言）
 * ⑨ 键盘路由（Esc/↑↓/Tab/空格/回车；**IME 组合期一律放行**；键名归一 `keyName`）⑩ 语音位（voiceBar 接线）。
 */
export const Composer = React.forwardRef<ComposerHandle, ComposerProps>(function Composer(props, ref) {
  const [text, setText] = useState(() => (props.draft && typeof props.draft.content === "string" ? props.draft.content : ""));

  const [menuState, setMenuState] = useState(() => emptyMenuState());
  const [notice, setNoticeState] = useState<{ msg: string; kind: ComposerNoticeKind } | null>(null);
  const inputEl = useRef<HTMLTextAreaElement | null>(null);
  const fileInputEl = useRef<HTMLInputElement | null>(null);
  const inert = !props.sessionId;

  const setNotice = (msg?: string | null, kind: ComposerNoticeKind = "info"): void => {
    setNoticeState(msg ? { msg, kind } : null);
  };
  const inlineNotice = notice?.msg ?? "";

  // ── ① 上传链状态机（B-21 三坑照抄）────────────────────────────────────────────
  const upload = useComposerUpload({
    sessionId: props.sessionId,
    ensureSessionId: props.ensureSession ?? null,
    notice: (msg, kind) => setNotice(msg ?? "", kind),
    onError: (msg) => setNotice(`附件上传失败：${msg}（栏内可重试，草稿未消费）`, "error"),
    ...(props.uploadDeps ?? {}),
  });
  const dropTarget = useComposerDropTarget({
    onFiles: (files: File[]) => upload.addFiles(files as never),
    notice: (m?: string, k?: ComposerNoticeKind) => setNotice(m ?? "", k ?? "info"),
  });
  useEffect(() => {
    dropTarget.attach();
    return () => dropTarget.detach();
    // 🔴 装/拆只认 attach/detach 身份（恒定）：拖放**进行中**的重渲染不得 detach（否则 dragging 被清、遮罩闪断）
  }, [dropTarget.attach, dropTarget.detach]);

  // ── ⑩ 语音位（整件重抄 102 `useComposerVoice.js` 语义；voiceMode＝hook 局部态，不写全局 store）──
  /** ★20261011（大王决策「语音落点参照旧树」）：**缺省真**——旧树 `ChatView.vue:461` 识别完即发。 */
  const voiceAutoSend = props.voiceAutoSend ?? true;
  const voice = useComposerVoice({
    notify: ((msg: string | VoiceNotice, kind?: "info" | "error") => {
      // ★F-05（册 §6.6）：带码提示形（`{code,text}`）⇒ 文案带上显式码；字符串形照旧
      if (msg && typeof msg === "object") {
        setNotice(`[${msg.code}] ${msg.text}`, msg.kind ?? kind ?? "error");
        return;
      }
      setNotice(msg ?? "", kind ?? "info");
    }) as NotifyFn,
    sttApi: (props as { sttApi?: never }).sttApi ?? sttTransport,
    // 识别成功：文本入框（可编辑可回车）；开关 `voiceAutoSend` 打开且可提交 ⇒ 直接发送；
    // 不可提交 ⇒ 退化为入框并写明原因（不丢字、不静默降级）——102 同口径。
    onRecognized: (recognized: string) => {
      setText((cur) => { const next = cur ? cur + recognized : recognized; pushDraft(next); return next; });
      if (!voiceAutoSend) {
        setNotice("已转写，确认后发送（可在 设置 › 输入 里开启「语音识别后自动发送」）");
        return;
      }
      if (canSubmitRef.current) void submitRef.current?.();
      else setNotice(`已转写，未自动发送：${guardReasonRef.current}`);
    },
    ...(props.voiceDeps ?? {}),   // 102 `...(props.voiceDeps || {})` 同位：注入面兜底真浏览器 API
  });
  const voiceOpen = voice.voiceMode;
  // 🔴 清理只认 dispose 身份（useCallback 恒定）：voice 对象每渲染换引用，若作依赖会**每次渲染 dispose**，
  //    disposedRef 置真 ⇒ 授权回来即停轨（录音必失效）。
  useEffect(() => () => voice.dispose(), [voice.dispose]);

  // ── ⑧ 真源 → 镜像（U-1c-FIX：父层给出**新草稿对象**本身＝一次真源断言；同值短路 ⇒ 不覆盖新输入）──
  const draftObj = props.draft;
  useEffect(() => {
    const v = draftObj && typeof draftObj.content === "string" ? draftObj.content : undefined;
    if (typeof v === "string") setText((cur) => (v !== cur ? v : cur));
  }, [draftObj, draftObj?.content]);

  /** 镜像 → 真源（编辑值每次变更即上抛；父层未受理则保持 ⇒ 输入不丢） */
  const pushDraft = (value: string): void => {
    props.onDraftChange?.({ content: value, files: (props.draft && Array.isArray(props.draft.files) ? props.draft.files : []) as readonly unknown[] });
  };

  // ── 命令词法（②）────────────────────────────────────────────────────────────
  const tokens = useMemo(() => tokenize(text), [text]);
  const isCommandLine = useMemo(() => isCommandLead(tokens), [tokens]);
  const refChips = useMemo(() => tokens.filter((t) => t.kind === "ref").map((t) => ({ name: (t as { name: string }).name, start: t.start, end: t.end })), [tokens]);

  // ── ④ 模型可用性（共享规则函数；未加载 ≠ 无可用）──────────────────────────────
  const modelUsable = useMemo(() => modelsUsable(props.modelCatalog as never), [props.modelCatalog]);
  /* ★F-38（L-PF-DFT-1 采甲，20261022 大王逐字「U-S5甲」）：会话级模型·默认兜底**唯一判定式**
     （`components/composer/modelFallback.resolveEffectiveModel`）：`model == null` ⇒ 取全局默认
     （调用方从配置读取位注入，禁硬编码模型名）；仍为空／读取失败／非法 ⇒ 显式拒＋带码可读原因。 */
  const effectiveModel = useMemo(() => {
    const meta = props.modelCatalog as { loaded?: boolean; groups?: readonly { items?: readonly { id?: unknown }[] }[] } | null | undefined;
    const catalogIds: string[] = [];
    if (meta && Array.isArray(meta.groups)) {
      for (const g of meta.groups) {
        for (const item of (g && Array.isArray(g.items) ? g.items : [])) {
          const id = item && typeof item.id === "string" ? item.id.trim() : "";
          if (id) catalogIds.push(id);
        }
      }
    }
    return resolveEffectiveModel({
      model: props.model,
      globalDefault: props.fallbackModel ?? null,
      globalDefaultError: props.fallbackReadError ?? null,
      catalogLoaded: meta?.loaded === true,
      catalogIds,
    });
  }, [props.model, props.fallbackModel, props.fallbackReadError, props.modelCatalog]);
  /* ★L-PL-5a 发送与模型解耦（账行动作逐字「模型面不得禁用发送键」，销「目录已装载∧未选模型」
     该禁用腿）：原「未选择模型」禁用腿与同名守卫原因腿一并退役 —— 未选择模型**不再**禁用发送；
     目录装载态派生量随之删除（判据 grep 该派生量标识 ⇒ 0 命中；标识逐字见总账 L-PL-5a 行）。
     保留：`modelsUsable` 腿（102 旧定案「无可用模型 ⇒ 停用而非隐藏」，账行未点名销）。 */
  /** §4 上行发送＝**声明动作**：宿主绑定（`onCommand`）为启用前提（`requires: host_binding`） */
  const send = SHELL_DECL.main.composer.send;
  const bound = typeof props.onCommand === "function";
  const hasSubmittable = text.trim().length > 0 || (props.draft?.files?.length ?? 0) > 0 || upload.items.length > 0;
  const canSubmit = useMemo(() => {
    if (!bound) return false;                       // 上行通道未绑定 ⇒ 显式禁用（不假装已发）
    if (props.pending > 0) return false;
    if (upload.hasError) return false;
    if (voice.busy) return false;                  // 102 L240：录音/识别中不可提交（互斥语义迁移）
    if (isCommandLine) return true;
    if (!modelUsable) return false;
    if (!effectiveModel.ok) return false;           // ★F-38：兜底决议失败（空/读取失败/非法）⇒ 显式拒
    return hasSubmittable;
  }, [bound, props.pending, upload.hasError, voice.busy, isCommandLine, modelUsable, effectiveModel, hasSubmittable]);
  /** 守卫原因（§7.1「守卫态必须可理解」：不得只禁用不解释） */
  const guardReason = useMemo(() => {
    // 模型面（102 逐字：无可用模型 ⇒ 停用而非隐藏）优先于绑定面说明（L-PL-5a 保留腿）
    if (!isCommandLine && !modelUsable) return "无可用模型";
    // ★F-38：兜底决议失败（空/读取失败/非法）⇒ 带显式码的可读原因（负例判据「出显式错误码」；守卫态必须可理解）
    if (!isCommandLine && !effectiveModel.ok) return modelGuardNote(effectiveModel);
    if (!bound) return send.absent_note;            // 声明缺席说明（逐字＝`main.composer.send.absent_note`）
    if (props.pending > 0) return "提交中";
    if (upload.hasError) return "存在上传失败的附件，请重试或移除";
    if (voice.isRecording) return "录音中，请先结束录音";   // 102 L255（可读原因，不只禁用）
    if (voice.isProcessing) return "语音识别中…";           // 102 L256
    if (!hasSubmittable) return "输入为空";
    return "";
  }, [bound, send, props.pending, upload.hasError, voice.isRecording, voice.isProcessing, isCommandLine, modelUsable, effectiveModel, hasSubmittable]);
  /** 发送口**是否可用**（声明面口径：宿主绑定 ＋ 有会话）；与「当前文本可否提交」分开：
   *  缺席说明只在**口不可用**时呈现（发完之后文本清空 ≠ 口不可用 ⇒ 不重复提示）。 */
  const sendReady = bound && !inert;
  /** ★ 主按钮语义（照 DSH primaryStops/primaryLabel；★20261011 大王决策「插话/排队照抄 dsh」）：
   *  繁忙期投递模式改由 **dsh `resolveSubmitMode` 逐字译件**（`assembly/input/submitMode.ts`）决议——
   *  不可插话 ⇒ `queue`（**排队发送**，不假发）；可插话 ⇒ 按偏好（缺省 `queue`）。
   *  加速手势（Cmd/Ctrl+Enter）在 `onKeydown` 里单独走 `steer`（dsh 同位语义）。 */
  const steerable = !!props.running && !props.stopping && !isCommandLine
    && text.trim().length > 0 && (props.draft?.files?.length ?? 0) === 0 && upload.items.length === 0;
  const busyMode = resolveSubmitMode({
    preferred: props.busyEnterMode ?? "queue",
    running: !!props.running,
    gesture: "plain",                 // 按钮/Enter 点击＝普通手势（加速手势见 onKeydown）
    steeringAvailable: steerable,
  });
  const primaryMode: "send" | "steer" | "queue" | "stop" | "stopping" = props.stopping
    ? "stopping"
    : (props.running ? (steerable ? busyMode : "stop") : "send");
  const primaryLabel = props.stopping ? "提交中…"
    : (props.running ? submitButtonLabel(busyMode, true) : (props.pending > 0 ? "提交中…" : "发送"));
  const primaryTitle = primaryMode === "steer"
    ? "插话发送（运行中：下一步生效；要停止本轮请先清空输入）"
    : (primaryMode === "queue" ? "排队发送（运行中：本轮结束后依次发送）" : (guardReason || "发送"));
  const modelLabel = (props.modelCatalog && (props.modelCatalog as { currentName?: string }).currentName) || props.model
    || (effectiveModel.ok ? effectiveModel.model : "");

  // ── ③ 候选菜单内核（纯内核；本组件只做渲染与路由）─────────────────────────────
  const menu = useMemo(() => createInputMenu({
    sources: [createCommandSource(), createRefSource({ getNames: () => props.refCandidates ?? [] })],
  }), [props.refCandidates]);
  const MENU_ID = "composer-menu";
  const menuCtx = useCallback(() => ({ model: props.model }), [props.model]);
  const menuOpen = menuState.open && menuState.items.length > 0;
  const activeDescendant = menuOpen ? menu.activeDescendantId(menuState) : null;

  const cursorOf = (el?: HTMLTextAreaElement | null): number => {
    const node = el ?? inputEl.current;
    return node && typeof node.selectionStart === "number" ? node.selectionStart : text.length;
  };
  const syncMenu = (el?: HTMLTextAreaElement | null, value?: string): void => {
    const t = value ?? text;
    setMenuState((s) => menu.reduce(s, { type: "cursor", text: t, cursor: cursorOf(el) }, menuCtx()));
  };
  const closeMenu = (): void => setMenuState((s) => (s.open ? menu.reduce(s, { type: "close" }, menuCtx()) : s));
  const settlePick = (content: string, cursor?: number): void => {
    setText(content); pushDraft(content);
    const at = typeof cursor === "number" ? cursor : content.length;
    requestAnimationFrame(() => {
      const el = inputEl.current;
      if (el && typeof el.focus === "function") el.focus();
      if (el && typeof el.setSelectionRange === "function") el.setSelectionRange(at, at);
      syncMenu(el, content);
    });
  };
  const onOptionDown = (item: MenuItem): void => {
    const applied = applyPick(text, menuState.token as { start: number; end: number } | null, item.value);
    settlePick(applied.content, applied.cursor);
  };
  const onOptionHover = (index: number): void => setMenuState((s) => menu.reduce(s, { type: "highlight", index }, menuCtx()));

  /** 键名归一（同一物理键别名合并：'enter'→'Enter'／'Spacebar'→' '；合成事件与老环境 keyCode 兜底） */
  const keyName = (event: React.KeyboardEvent): string => {
    const raw = typeof event.key === "string" ? event.key : "";
    const code = Number((event as unknown as { keyCode?: number; which?: number }).keyCode || (event as unknown as { which?: number }).which || 0);
    if (raw === " " || raw === "Spacebar" || code === 32) return " ";
    if (/^enter$/i.test(raw) || raw === "Return" || code === 13) return "Enter";
    return raw;
  };

  function onInput(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    const el = event.target;
    setText(el.value); pushDraft(el.value); setNotice(null); syncMenu(el, el.value);
  }

  /** ⑨ 键盘路由（§7.1 裁决行／Tab 行；顺序＝Esc→↑↓→Tab→空格/回车裁决→回车兜底提交） */
  function onKeydown(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
    const key = keyName(event);
    if (key === "Escape") { if (!menuOpen) return; event.preventDefault(); closeMenu(); return; }
    if (key === "ArrowDown" || key === "ArrowUp") {
      if (!menuOpen) return;
      event.preventDefault();
      setMenuState((s) => menu.reduce(s, { type: "move", delta: key === "ArrowDown" ? 1 : -1 }, menuCtx()));
      return;
    }
    if (key === "Tab") {
      syncMenu(inputEl.current);
      const outcome = menu.tabOutcome(menuState);
      if (!outcome) return;                       // 🔴 无高亮 ⇒ 原样放行（不吞 Tab，保原生焦点遍历）
      if (outcome.action === MENU_ACTION.DRILL) return;
      event.preventDefault(); onOptionDown(outcome.item); return;
    }
    const hookInput = () => ({ state: menuState, text, cursor: cursorOf(inputEl.current) });
    if (key === " ") {
      if (event.shiftKey) return;
      if (event.nativeEvent.isComposing || (event as unknown as { keyCode?: number }).keyCode === 229) return; // IME 组合期放行
      const hit = menu.resolve("matchSpace", hookInput(), menuCtx());
      if (hit && hit.result && hit.result.handled) { event.preventDefault(); settlePick(hit.result.content, hit.result.cursor); }
      return;
    }
    if (key === "Enter") {
      if (event.shiftKey) return;
      if (event.nativeEvent.isComposing || (event as unknown as { keyCode?: number }).keyCode === 229) return; // IME 组合期不提交
      const hit = menu.resolve("matchEnter", hookInput(), menuCtx());
      if (hit && hit.result && hit.result.handled) { event.preventDefault(); settlePick(hit.result.content, hit.result.cursor); return; }
      event.preventDefault(); void submit(); return;
    }
  }

  /** ★A-044② 发送结果判定：宿主显式返回 `false` ＝ 发送失败（保留输入＋显式原因）；`true`／不返回 ＝ 成功 ⇒ 清空 */
  const SEND_FAIL_NOTE = "发送失败（上行通道不可用：连接未就绪或宿主拒发）：输入已保留，可重发";

  /** 成功 ⇒ 清空输入（A-044②：发送成功不留存输入框） */
  function clearAfterSend(): void {
    setText("");
    pushDraft("");
    syncMenu(null, "");
  }

  /** 提交（Enter／发送共用）：已知命令 ⇒ kind:'command'（frozen）；未知 ⇒ **作为文本发送 ＋ 行内提示**（不静默）；其余 ⇒ prompt。
   *  ★A-044②（20261006）统一口径（对治报障「发送成功却留存输入框」）：**成功 ⇒ 输入清空；失败/被拒 ⇒ 保留＋显式原因**。 */
  async function submit(): Promise<void> {
    // ★AUFET02（L-AU-FE-T02）**上行静默丢失**：上游未绑定（未连 ⇒ 宿主传 `undefined`，而非「恒返回 false 的
    //   函数」）⇒ 本处**显式提示**（禁「点了没反应」）：按钮虽已 `disabled`，Enter／语音自动发送／命令面板
    //   仍可到达本函数。输入**保留**（不清空、不假发、不伪造回流）。
    if (!bound) { setNotice(send.absent_note + "（消息未发出，输入已保留）", "error"); return; }
    if (!canSubmit) return;
    setNotice(null);
    // ★20261011（大王决策「插话/排队照抄 dsh」）：繁忙期**插话**投递——线上 `kind:"steer"`
    //   （后端 `command_ingress.py` 闭集已含 steer；`chat_wiring` 对 stop|steer 同口受理）。
    //   只在「运行中 ∧ 可插话 ∧ 模式决议=steer」时启用；否则照旧走 `command`（含排队路径）。
    if (props.running && !props.stopping && steerable && busyMode === "steer" && !isCommandLine) {
      if (props.onCommand?.(text.trim(), { files: [], sessionId: typeof props.sessionId === "string" ? props.sessionId : "", kind: "steer", model: effectiveModel.ok ? effectiveModel.model : undefined }) === false) {
        setNotice(SEND_FAIL_NOTE, "error");
        return;
      }
      clearAfterSend();
      return;
    }
    // ★20261011（照抄 dsh）：运行中 ＋ 排队模式 ⇒ **入本地队列**（不出网；运行结束依次投）
    if (props.running && !props.stopping && busyMode === "queue" && !isCommandLine && text.trim() !== "") {
      queue.enqueue(text.trim());
      clearAfterSend();
      return;
    }
    if (isCommandLine) {
      const cmd = tokens[0] as { name: string; end: number };
      if (!COMMANDS.has("/" + cmd.name)) {
        // 未知命令 ⇒ 作为文本发送（102 口径，行内提示）；A-044②：发送成功 ⇒ 清空（不留存）；失败 ⇒ 保留＋原因
        setNotice(unknownCommandNotice("/" + cmd.name), "error");
        if (props.onCommand?.(text.trim()) === false) {
          setNotice(unknownCommandNotice("/" + cmd.name) + "；" + SEND_FAIL_NOTE, "error");
          return;
        }
        clearAfterSend();
        return;
      }
      if (props.onCommandLine?.({ name: "/" + cmd.name, argument: text.slice(cmd.end).trim(), mode: "frozen" }) === false) {
        setNotice("命令提交失败（上行通道不可用）：输入已保留，可重发", "error");
        return;
      }
      clearAfterSend();
      return;
    }
    if (upload.hasPending || upload.items.length) {
      const submittedText = text.trim();   // 🔴 正文在**进入上传编排之前**取值（宿主切会话即清输入的语义）
      const r = (await upload.submitUploads()) as unknown as { ignored?: boolean; blocked?: string; metas?: readonly AttachmentMeta[]; sessionId?: string };
      if (r.ignored) return;
      if (r.blocked === "no-session") { setNotice("未选择会话：附件须先归属会话（P3 门）", "error"); return; }
      if (r.blocked) { setNotice("附件上传未完成，发送已拦停（栏内重试或移除；草稿未消费）", "error"); return; }
      // A-044②：附件路径同口径 —— 成功 ⇒ 清空正文；失败 ⇒ 保留＋原因（不再"发送成功却留存"）
      if (props.onCommand?.(submittedText, { files: r.metas ?? [], sessionId: r.sessionId ?? "", model: effectiveModel.ok ? effectiveModel.model : undefined }) === false) {
        setNotice("发送失败（上行通道不可用）：附件已上传、正文输入已保留，可重发", "error");
        return;
      }
      clearAfterSend();
      return;
    }
    const sid = typeof props.sessionId === "string" ? props.sessionId : null;
    if (sid === "") { setNotice("未选择会话，消息未发出（输入已保留；请先在左栏新建/选择一个会话）", "error"); return; }
    // A-044②：**成功 ⇒ 清空；失败 ⇒ 保留＋显式原因**（成功判定＝宿主未显式返回 `false`；
    // `command_ack` 若为 rejected/error ⇒ 下方 outbox effect 把该条文本放回输入框，可重发）。
    if (props.onCommand?.(text.trim()) === false) {
      setNotice(SEND_FAIL_NOTE, "error");
      return;
    }
    clearAfterSend();
  }

  function cancel(): void { props.onStop?.(); }
  function focus(): void { const el = inputEl.current; if (el) el.focus(); }
  function openFilePicker(): void { const el = fileInputEl.current; if (el) el.click(); }
  function removeChip(chip: { start: number; end: number }): void {
    const next = text.slice(0, chip.start) + text.slice(chip.end);
    setText(next); pushDraft(next);
  }
  function onAttachClick(): void { openFilePicker(); }
  function onFileInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const el = event.target;
    const files = el && el.files ? Array.prototype.slice.call(el.files) : [];
    if (el) el.value = "";
    if (files.length) upload.addFiles(files);
  }
  useImperativeHandle(ref, () => ({ submit, cancel, focus, openFilePicker }), [submit, cancel, focus, openFilePicker]);

  const canSubmitRef = useRef(canSubmit); const guardReasonRef = useRef(guardReason); const submitRef = useRef<null | (() => void | Promise<void>)>(null);
  useEffect(() => { canSubmitRef.current = canSubmit; guardReasonRef.current = guardReason; submitRef.current = submit; });

  // ── 上行回执行（乐观「已受理中」→ `command_ack` 三态）────────────────────────
  // 纪律：**禁编造助手内容** —— 本列只回显**自己发出的请求文本**与其**服务端回执**；
  //       `rejected`／`error` ⇒ 显式错误落 `inlineNotice` ＋ 文本**放回输入框**（可重发）。
  const outbox = props.outbox ?? [];
  const handledAcks = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const row of outbox) {
      if (row.status === "pending" || handledAcks.current.has(row.localId)) continue;
      handledAcks.current.add(row.localId);
      if (row.status === "accepted") continue;
      setNotice(`命令未受理（${row.error || row.status}）：输入已保留，可重发`, "error");
      setText((cur) => {
        if (cur !== "") return cur;                 // 用户已另起输入 ⇒ 不覆盖（不丢字）
        const next = row.text;
        pushDraft(next);
        return next;
      });
    }
  }, [outbox]);

  // ── ⑪ 本地发送队列（★20261011 大王决策「插话/排队照抄 dsh」；dsh＝客户端本地队列＋坞内三键）──
  const queue = useComposerQueue({
    running: !!props.running,
    // 真投递（运行结束依次投；`kind:"command"` 与直发同源，不另造通道）
    // ★AUFET02（L-AU-FE-T02）**上行静默丢失**：宿主未绑定（未连 ⇒ `App.tsx:296` 传 `undefined`）时原式
    //   `props.onCommand?.(…) !== false` ⇒ `undefined !== false` ＝ **true** ＝「投递成功」⇒ `useComposerQueue`
    //   （`:113 if (ok) setItems(…filter…)`）按成功**出队**：文本静默消失、零出网、无 failed 标、无提示。
    //   ⇒ 未绑定一律判**失败**：留队 ＋ 标「投递被拒」（与 `useComposerQueue` 头注纪律「投递被拒 ⇒ 留队＋标
    //   failed（不假发）」同口径；不新造通道、不缓存重放）。
    deliver: (qtext: string) => (props.onCommand
      ? props.onCommand(qtext, { files: [], sessionId: typeof props.sessionId === "string" ? props.sessionId : "", model: effectiveModel.ok ? effectiveModel.model : undefined }) !== false
      : false),
    // 坞内「插话」＝提前投递（dsh `queue.steer`）⇒ 线上走 `kind:"steer"`（同上：未绑定判失败，留队可重试）
    onSteer: (qtext: string) => (props.onCommand
      ? props.onCommand(qtext, { files: [], sessionId: typeof props.sessionId === "string" ? props.sessionId : "", kind: "steer", model: effectiveModel.ok ? effectiveModel.model : undefined }) !== false
      : false),
  });

  const isTakenOver = !!props.takeover;
  return (
    <div id="xg-input-area" className="composer" data-testid="composer">
      {isTakenOver ? (
        <div className="composer-takeover" data-testid="composer-takeover" role="group" aria-label="接管态">
          <span className="xg-sheet-handle" aria-hidden="true" />
          <button
            type="button"
            className="xg-btn"
            data-testid="composer-takeover-close"
            onClick={props.onOpenTakeover}
            style={{ alignSelf: "flex-end", marginRight: "var(--xg-space-3)" }}
          >关闭</button>
          {props.takeover}
        </div>
      ) : (
        <>
          {refChips.length ? (
            <div className="composer-chips" data-testid="composer-chips">
              {refChips.map((chip) => (
                <span key={chip.start + ":" + chip.name} className="chip" title={"引用 " + chip.name}>
                  @{chip.name}
                  <button className="chip-x" type="button" aria-label={"移除引用 " + chip.name} onClick={() => removeChip(chip)}>✕</button>
                </span>
              ))}
            </div>
          ) : null}

          {!voiceOpen ? (
            <textarea
              ref={inputEl}
              className="composer-input"
              data-testid={props.inputTestid ?? "composer-input"}
              rows={1}
              enterKeyHint="send"
              role="combobox"
              aria-autocomplete="list"
              aria-haspopup="listbox"
              aria-label="消息输入"
              aria-expanded={menuOpen ? "true" : "false"}
              aria-controls={menuOpen ? MENU_ID : undefined}
              aria-activedescendant={activeDescendant ?? undefined}
              value={text}
              placeholder="输入消息，/ 命令，@ 引用"
              disabled={inert}
              onChange={onInput}
              onKeyDown={onKeydown}
              onClick={() => syncMenu(inputEl.current)}
              onBlur={closeMenu}
            />
          ) : (
            <button
              type="button"
              className="composer-talk"
              data-testid="composer-talk"
              aria-label={voice.isRecording ? "结束录音并发送" : "开始录音（或按住说话）"}
              data-recording={voice.isRecording ? "true" : "false"}
              onMouseDown={(e) => { e.preventDefault(); voice.startRecording(); }}
              onMouseUp={(e) => { e.preventDefault(); voice.stopRecording(); }}
              onMouseLeave={() => { if (voice.isRecording) voice.stopRecording(); }}
              onTouchStart={(e) => { e.preventDefault(); voice.startRecording(); }}
              onTouchEnd={(e) => { e.preventDefault(); voice.stopRecording(); }}
              onClick={(e) => { if (e.detail !== 0) return; if (voice.isRecording) voice.stopRecording(); else voice.startRecording(); }}
            >{voice.isRecording ? "松开结束录音" : (voice.isProcessing ? "识别中…" : "按住说话")}</button>
          )}

          <div className="composer-row">
            <button className="composer-attach" type="button" data-testid="composer-attach" aria-label="添加附件" onClick={onAttachClick}><XgIcon name="paperclip" size={16} /></button>
            <button
              className="composer-mic"
              type="button"
              data-testid="composer-mic"
              aria-pressed={voiceOpen ? "true" : "false"}
              aria-label={voiceOpen ? "退出语音模式" : "语音输入（切换）"}
              disabled={props.voiceAbsent || (voice.busy && !voice.isRecording)}
              onClick={() => voice.toggleVoiceMode()}
            ><XgIcon name="mic" size={16} /></button>
            <button
              type="button"
              className="composer-model"
              data-testid={props.modelSlotTestid ?? "composer-model-slot"}
              data-model={effectiveModel.ok ? effectiveModel.model : (props.model ?? "")}
              data-model-available={modelUsable ? "true" : "false"}
              aria-expanded={props.modelPanelOpen ? "true" : "false"}
              aria-haspopup="dialog"
              aria-label={"模型：" + (modelLabel || "选择模型")}
              title={modelUsable ? "当前模型：" + (modelLabel || "未选择") : "无可用模型"}
              onClick={() => props.onOpenModel?.()}
            >
              <span className="composer-model__label">{modelLabel || "选择模型"}</span>
              <span className="composer-model__caret" aria-hidden="true">▾</span>
            </button>
            <input ref={fileInputEl} type="file" multiple hidden className="composer-file" data-testid="composer-file-input" aria-hidden="true" tabIndex={-1} onChange={onFileInputChange} />
            {primaryMode === "stop" || primaryMode === "stopping" ? (
              <button
                className="composer-stop"
                type="button"
                data-testid={props.stopTestid ?? "composer-stop"}
                disabled={primaryMode === "stopping"}
                title={primaryMode === "stopping" ? "停止中…（终态未到）" : "停止本轮（取消粒度＝轮级；已派发的工具可能仍会执行完）"}
                onClick={cancel}
              >{primaryMode === "stopping" ? "停止中…" : "停止"}</button>
            ) : (
              <button
                className="composer-send"
                type="button"
                data-testid={props.sendTestid ?? "composer-send"}
                data-action={send.action}
                data-bound={bound ? "true" : "false"}
                aria-label={send.aria_label}
                aria-disabled={sendReady ? undefined : "true"}
                aria-describedby={sendReady ? undefined : "composer-send-reason"}
                disabled={!canSubmit}
                title={primaryTitle}
                onClick={() => void submit()}
              >{primaryLabel}</button>
            )}
          </div>

          {/* 上行不可用原因：**可见**（声明 `main.composer.send.absent_note`；无会话另记）
              ——禁「点了没反应」、禁假装已发（不清输入、不伪造回流）。 */}
          {sendReady ? null : (
            <p
              id="composer-send-reason"
              data-testid="composer-send-reason"
              className="xg-hint"
              style={{ margin: "0 var(--xg-space-3) var(--xg-space-2)" }}
            >{inert ? "无会话 ⇒ 输入区 inert；" : ""}{bound ? "" : send.absent_note}</p>
          )}

          {/* ★20261011（大王口径逐字「发送成功不用在输入框底部…打对号，收入失败更不用」）：
              **删「上行回执行」**（原 `xg-outbox`＋`outbox-state-<id>` 的 ✓ 徽标）。依据＝两路调研一致：
              dsh 成功＝**乐观提交**（提交即清草稿）＋消息流回显、成功路径 `return []`（不产生任何 notice）；
              旧树成功＝**静默**（`setNotice('')`），输入区下方那条 notice 只在失败/拦截/插话时出现。
              ⇒ 两边都**没有**「发送成功对号」这种形态，本处原属**自造**。
              失败面**不丢**：`rejected`/`error` 仍由上方 effect 出**行内 notice**（role=alert）＋文本**放回输入框**
              ⇒ 只是不再占用输入框底部的常驻位。 */}

          {/* ★20261011（大王决策「插话/排队照抄 dsh」）：**本地发送队列坞**（dsh QueueDock 同功能，位置按本仓纪律）。
              每行＝条文本＋状态（`发送中…`／失败原因）＋三键：**编辑／删除／插话**（dsh `queue.edit/remove/steer` 同名语义）。
              落点说明见 `shell/useComposerQueue.ts` 头注（dsh 在流内、本仓流由投影驱动 ⇒ 本地队列不塞流内）。 */}
          {queue.items.length ? (
            <div className="xg-queue-dock" data-testid="composer-queue-dock" role="list" aria-label="待发送队列">
              {queue.items.map((it) => (
                <div className="xg-queue-dock__row" role="listitem" key={it.id} data-testid={`queue-row-${it.id}`} data-status={it.status}>
                  <span className="xg-queue-dock__text" title={it.text}>{it.text}</span>
                  {it.status === "sending" ? (
                    <span className="xg-queue-dock__status" role="status">发送中…</span>
                  ) : null}
                  {it.status === "failed" ? (
                    <span className="xg-queue-dock__status" data-state="failed" title={it.error}>{it.error || "投递被拒"}</span>
                  ) : null}
                  <button
                    type="button"
                    className="xg-queue-dock__act"
                    aria-label={`编辑排队消息 ${it.text}`}
                    onClick={() => { setText(it.text); pushDraft(it.text); queue.remove(it.id); }}
                  >编辑</button>
                  <button
                    type="button"
                    className="xg-queue-dock__act"
                    aria-label={`删除排队消息 ${it.text}`}
                    onClick={() => queue.remove(it.id)}
                  >删除</button>
                  <button
                    type="button"
                    className="xg-queue-dock__act"
                    aria-label={`插话发送 ${it.text}`}
                    disabled={it.status === "sending"}
                    onClick={() => queue.steer(it.id)}
                  >插话</button>
                </div>
              ))}
            </div>
          ) : null}

          <ComposerUploadBar
            items={upload.items}
            waitIds={upload.waitIds}
            onRemove={(id: string) => upload.removeAttachment(id)}
            onMove={({ id, delta }: { id: string; delta: number }) => upload.moveAttachment(id, delta)}
            onRetry={(id: string) => upload.retryUpload(id)}
          />

          {menuOpen ? (
            <div id={MENU_ID} className="composer-menu" role="listbox" data-testid="composer-menu" aria-label="候选">
              {menuState.items.map((item, i) => (
                <div
                  key={item.id}
                  id={menu.optionId(i)}
                  className="composer-menu-item"
                  role="option"
                  aria-selected={i === menuState.highlight ? "true" : "false"}
                  data-active={i === menuState.highlight ? "true" : "false"}
                  onMouseDown={(e) => { e.preventDefault(); onOptionDown(item); }}
                  onMouseEnter={() => onOptionHover(i)}
                >
                  <span className="composer-menu-item__label">{item.label}</span>
                  {item.describe ? <span className="composer-menu-item__desc">{item.describe}</span> : null}
                </div>
              ))}
            </div>
          ) : null}

          {inlineNotice ? <div className="composer-notice" data-testid="composer-error" role="alert">{inlineNotice}</div> : null}
        </>
      )}
      <ComposerDropOverlay visible={dropTarget.dragging} />
    </div>
  );
});
